import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";

import styles from "./moonshot.module.scss";

const Moonshot = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const smallDevice = windowWidth <= 990;
  const largeDevice = windowWidth > 990;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    setWindowWidth(window.innerWidth);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: "#moonshotWrapper",
        scrub: true,
        end: "+=750",
      },
    });

    const moonTimeline = gsap.timeline();
    moonTimeline.from("#moon", {
      y: 350,
      duration: 90,
      opacity: 0.8,
      delay: 4,
    });

    const textTimeline = gsap.timeline();
    textTimeline.from("#textContent", {
      y: 500,
      duration: 95,
      opacity: 0.5,
      delay: 5,
    });

    mainTimeline.add(moonTimeline, 12);
    mainTimeline.add(textTimeline, 24);
  }, []);

  return (
    <section className={styles.moonshotWrapper} id="moonshotWrapper">
      <div className={styles.sceneSection}>
        <div className={styles.moonWrapper}>
          <img
            className={styles.moon}
            id="moon"
            src={"/images/moonshot/moon.png"}
            alt=""
          />
          <div className={styles.textContent} id="textContent">
            <p className={styles.theMoonshot}>
              <span>The</span>
              <span>Moonshot!</span>
            </p>
            <p className={styles.staffFavorite}>Our Staff Favorite</p>
            <a href="https://campaigns-grammarly.cleverstory.io/the-productivity-shift-ROI/the-productivity-shift" target="_blank">
              <img
                className={styles.moonshotLogo}
                src={"/images/moonshot/hbxLogo.png"}
              />
            </a>
          </div>
        </div>
        <img
          className={styles.terrainImg}
          src={
            largeDevice
              ? "/images/moonshot/terrain_desktop.png"
              : "/images/moonshot/terrain_mobile.png"
          }
        />
        <img
          className={styles.astronaut}
          src={"/images/moonshot/astronaut.png"}
          alt=""
        />
      </div>

      <div className={styles.contentSection}>
        {largeDevice &&
          <div className={styles.imageSection}>
            <img
              className={styles.websiteImg} src="/images/moonshot/hbx.png"
            />

            <img
              className={styles.builtIn3days}
              src={"/images/moonshot/builtIn3days.png"}
              alt=""
            />
            <img
              className={styles.builtWithCleverstory}
              src={"/images/moonshot/builtWithCleverstory.png"}
              alt=""
            />
          </div>
        }
        <div className={styles.textSection}>
          <p className={styles.timeline}>JANUARY, 2026</p>
          <h2 className={styles.heading}>
            How HBX Hotel Beds Shares Its Sustainability Journey Into a Shared Resource
          </h2>

          <div className={styles.QnA}>
            {smallDevice &&
              <div className={styles.imageSection}>
                <img 
                  className={styles.websiteImg} src="/images/moonshot/hbx.png"
                />

                <img
                  className={styles.builtIn3days}
                  src={"/images/moonshot/builtIn3days.png"}
                  alt=""
                />
                <img
                  className={styles.builtWithCleverstory}
                  src={"/images/moonshot/builtWithCleverstory.png"}
                  alt=""
                />
              </div>
            }
            <div className={styles.answer}>
              <span className={styles.paragraph}>
                You've booked your New Year's getaway. Maybe a ski resort. Maybe somewhere warm to escape winter. Simple reservation, done. Behind that booking? A complex web of suppliers trying to meet strict sustainability requirements.
              </span>

              <span className={styles.paragraph}>
                HBX Hotel Beds connects hotels, tour operators, and travel services globally. When they started their own sustainability journey (ISO certifications, ESG reporting, carbon tracking), they realized their partners were drowning in the same compliance requirements without the budgets or teams to handle it.
              </span>

              <span className={styles.paragraph}>
                European laws now require all businesses to report on sustainability. Carbon emissions, environmental certifications, social impact. Big hotel chains have entire teams for this. Small family-run hotels are figuring it out alone. < br/>
                Since HBX was already documenting everything internally, they asked: why not share it?
              </span>

              <span className={styles.paragraph}>
                The problem: PDFs don't get read. Static web pages get ignored. And HBX had no idea who was engaging with their content or what partners actually needed.
              </span>

              <span className={styles.paragraph}>
                With Cleverstory, they rebuilt their sustainability content as interactive experiences. Articles, newsletters, webinars, case studies. Videos, dynamic elements, and real examples from their certification journey.
                "This kind of content is sometimes hard," says Carlota from their team. "We made it interactive. You can upload videos, move elements, create dynamics instead of static articles."
              </span>

              <span className={styles.paragraph}>
                Now HBX sees which partners explore specific pages, frameworks, case study revisits, and what content actually helps. It's not vanity metrics. It's intel on what their ecosystem needs.
              </span>

              <span className={styles.paragraph}>
                They know what resonates and where people drop off. Everything lives on their website. No external platforms. No disappearing PDFs.
              </span>

              <span className={styles.paragraph}>
                HBX is using their position to pull their entire network forward. Because when everyone faces the same regulations, helping them isn't charity. It's a strategy to be an SME in the community.
              </span>

              <span className={styles.paragraph}>
                They turned internal docs into a shared resource. With Cleverstory, they made it usable and measurable.
              </span>
            </div>
            <div className={styles.liveExperienceLink}>
              <a href="https://employeejourney.hotelbeds.com/home-page" target="_blank">
                Live Experience
                <img className={styles.shortArrow} src={"/images/moonshot/shortArrow.png"} alt=""
              />
              </a>
            </div>
            {smallDevice &&
              <img
                className={styles.astronautImage}
                src={"/images/astronaut.png"}
                alt=""
              />
            }
          </div>
        </div>
      </div>
    </section>
  );
};

export default Moonshot;
